<template>
  <div class="store-report">
    <!-- 查询条件区 -->
    <div class="report-query-panel">
      <div class="query-title">查询条件</div>
      <div class="query-row">
        <el-select v-model="reportType" placeholder="报告类型" size="small"
                   class="qf qf-narrow" @change="onReportTypeChange">
          <el-option :value="1" label="月度" />
          <el-option :value="2" label="季度" />
          <el-option :value="3" label="年度" />
        </el-select>

        <el-select v-model="formKey" placeholder="问卷" size="small" class="qf qf-form" @change="onFormChange" v-if="formList.length">
          <el-option v-for="form in formList" :key="getFormKey(form)" :label="stripHtml(form.name)" :value="getFormKey(form)" />
        </el-select>

        <el-select v-model="year" placeholder="年度" size="small" class="qf qf-narrow" @change="onYearChange">
          <el-option v-for="y in yearOptions" :key="y" :label="y + '年'" :value="y" />
        </el-select>

        <el-select v-if="reportType === 1" v-model="month" placeholder="月份" size="small" class="qf qf-narrow">
          <el-option v-for="m in monthOptions" :key="m.value" :label="m.label" :value="m.value">
            <span style="float:left">{{ m.label }}</span>
            <span style="float:right;color:#94a3b8;font-size:12px">{{ m.hasSnapshot ? '已生成' : '未生成' }}</span>
          </el-option>
        </el-select>

        <el-select v-if="reportType === 2" v-model="quarter" placeholder="季度" size="small" class="qf qf-narrow">
          <el-option v-for="q in quarterOptions" :key="q.value" :label="q.label" :value="q.value" :disabled="q.disabled">
            <span style="float:left">{{ q.label }}</span>
            <span style="float:right;color:#94a3b8;font-size:12px" v-if="!q.hasSnapshot">未生成</span>
          </el-option>
        </el-select>

        <el-select v-model="daquId" placeholder="战区" size="small" clearable class="qf qf-narrow" @change="onDaquChange">
          <el-option v-for="d in daquList" :key="d.id" :label="d.name" :value="d.id" />
        </el-select>

        <el-select v-model="xiaoquId" placeholder="单元" size="small" clearable class="qf qf-narrow">
          <el-option v-for="x in filteredXiaoquList" :key="x.id" :label="x.name" :value="x.id" />
        </el-select>

        <el-input v-model="storeKeyword" placeholder="门店编号/名称" prefix-icon="el-icon-search" size="small" class="qf qf-search" clearable />

        <el-button type="primary" size="small" @click="handleQuery">确定</el-button>
        <el-button size="small" @click="handleReset">重置</el-button>
      </div>
    </div>

    <!-- 标题 + 操作 -->
    <div class="report-title-bar">
      <h2 class="report-title">{{ reportTitle }}</h2>
      <div class="title-actions">
        <el-button size="small" @click="showGenerateDialog = true">生成/更新统计</el-button>
        <el-button v-if="snapshotInfo && snapshotInfo.isPublished" size="small" :loading="publishing" @click="unpublishReport">取消发布</el-button>
        <el-button v-else-if="snapshotInfo" size="small" :loading="publishing" @click="publishReport">发布</el-button>
        <el-button size="small" type="text" class="export-link" @click="exportExcel">
          <i class="el-icon-download"></i> 导出查询结果
        </el-button>
      </div>
    </div>

    <!-- 信息条 -->
    <div class="info-bar" v-if="hasData">
      <span v-if="snapshotInfo">数据更新: {{ snapshotInfo.generateTime | formatTime }}</span>
      <span v-else>实时计算（未保存快照）</span>
      <span class="sep">·</span>
      <span>统计门店: {{ total }}</span>
      <span class="sep" v-if="scoringRuleLabel">·</span>
      <span v-if="scoringRuleLabel">算分规则: {{ scoringRuleLabel }}</span>
      <span class="sep" v-if="snapshotInfo">·</span>
      <el-tag v-if="snapshotInfo && snapshotInfo.isPublished" size="mini" type="success" effect="plain">已发布</el-tag>
      <el-tag v-else-if="snapshotInfo" size="mini" type="info" effect="plain">未发布</el-tag>
    </div>

    <!-- Tab 切换 -->
    <el-radio-group v-model="metricType" size="small" class="metric-tabs">
      <el-radio-button :label="1">指标得分</el-radio-button>
      <el-radio-button :label="2">指标落实率</el-radio-button>
      <el-radio-button :label="3">各选项占比</el-radio-button>
    </el-radio-group>

    <!-- 横版报表 -->
    <MultiLevelReportTable
      :data="sortedRows"
      :segment-columns="segmentColumns"
      :item-columns="itemColumns"
      :option-rates="optionRates"
      :metric-type="metricType"
      :scoring-rule-code="scoringRuleCode"
      :report-type="reportType"
      :query-context="{ businessType, businessTypeLabel, year, month, quarter }"
      :total="total"
      :current-page="page"
      :page-size="pageSize"
      :loading="loading"
      @page-change="onPageChange"
      @size-change="onSizeChange"
      @sort-change="onSortChange"
    />

    <!-- 空状态 -->
    <div v-if="!loading && !hasData && !errorMsg" class="empty-tip">
      请选择查询条件后点击「确定」加载报告
    </div>
    <div v-if="errorMsg" class="empty-tip warn">{{ errorMsg }}</div>

    <!-- 生成弹窗 -->
    <GenerateReportDialog
      :visible.sync="showGenerateDialog"
      :project-id="projectId"
      :form-key="formKey"
      :form-name="currentFormName"
      @success="onGenerateSuccess"
    />

    <!-- 发布弹窗（月度+无快照定位时使用旧波次发布；已统一到新 snapshotId 路径下，简化为纯确认弹窗） -->
    <el-dialog
      title="发布报告"
      :visible.sync="publishDialogVisible"
      width="420px"
    >
      <p>将当前快照（{{ snapshotInfo && snapshotInfo.periodLabel }}）发布到客户门户，确认吗？</p>
      <div slot="footer">
        <el-button size="small" @click="publishDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="publishing" @click="confirmPublish">确定发布</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'index/axios'
import { getProjectForms } from '@/app/index/api/project/manager'
import { getWaveList } from 'index/api/project/wave'
import * as XLSX from 'xlsx'
import GenerateReportDialog from './components/GenerateReportDialog.vue'
import {
  flattenStoreRows,
  ensureLetterCodes,
  buildReportTitle,
  buildPeriodLabel
} from '@/app/survey/utils/reportProcessor'

const RULE_LABELS = {
  simple: '原始累加',
  percentage: '百分制',
  bonusNormalized: '加分题百分制',
  custom: '自定义'
}

export default {
  name: 'StoreReport',
  components: { GenerateReportDialog },
  data () {
    const cur = new Date()
    return {
      projectId: this.$route.params.projectId,

      // 查询条件
      reportType: 1,
      formKey: '',
      businessType: null,
      businessTypeLabel: '',
      year: cur.getFullYear(),
      month: null,
      quarter: null,
      daquId: null,
      xiaoquId: null,
      storeKeyword: '',
      metricType: 1,

      // 下拉数据
      formList: [],
      allFormList: [],
      daquList: [],
      xiaoquList: [],
      allSources: [], // /Report/availableSources 全量

      // 表格数据
      rows: [],
      segmentColumns: [],
      itemColumns: [],
      optionRates: {},
      scoringRuleCode: 'simple',
      scoringRuleLabel: '原始累加',
      total: 0,
      page: 1,
      pageSize: 10,

      // 状态
      loading: false,
      generating: false,
      publishing: false,
      showGenerateDialog: false,
      publishDialogVisible: false,
      snapshotInfo: null,
      errorMsg: '',

      // v1.5 修复 P2-03：当前页内排序
      sortField: '',
      sortOrder: ''
    }
  },
  filters: {
    formatTime (val) {
      if (!val) return '-'
      const d = new Date(val)
      return d.toLocaleString('zh-CN')
    }
  },
  computed: {
    yearOptions () {
      const cur = new Date().getFullYear()
      return [cur - 2, cur - 1, cur, cur + 1]
    },
    availablePeriodsForCurrentType () {
      return (this.allSources || []).filter(s =>
        s.reportType === this.reportType && s.year === this.year)
    },
    monthOptions () {
      const set = new Set(this.availablePeriodsForCurrentType
        .filter(s => s.month).map(s => s.month))
      return Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: (i + 1) + '月',
        hasSnapshot: set.has(i + 1),
        disabled: false
      }))
    },
    quarterOptions () {
      const set = new Set(this.availablePeriodsForCurrentType
        .filter(s => s.quarter).map(s => s.quarter))
      return [1, 2, 3, 4].map(q => ({
        value: q,
        label: 'Q' + q,
        hasSnapshot: set.has(q),
        disabled: !set.has(q)
      }))
    },
    filteredXiaoquList () {
      if (!this.daquId) return this.xiaoquList
      return this.xiaoquList.filter(x => x.daquId === this.daquId || x.parentId === this.daquId)
    },
    reportTitle () {
      return buildReportTitle(this.businessTypeLabel || this.currentFormName, this.reportType, this.year, this.month, this.quarter)
    },
    currentFormName () {
      if (!this.formKey || !this.formList.length) return ''
      const form = this.formList.find(f => this.getFormKey(f) === this.formKey)
      return form ? this.stripHtml(form.name) : ''
    },
    hasData () {
      return this.rows && this.rows.length > 0
    },
    /**
     * v1.5 修复 P2-03：当前页 rows 的排序视图。
     * 因为后端已分页，这里仅在当前页内排序；如需全局排序应改后端 OrderBy。
     */
    sortedRows () {
      if (!this.sortField || !this.sortOrder) return this.rows
      const arr = (this.rows || []).slice()
      const dir = this.sortOrder === 'descending' ? -1 : 1
      const key = this.sortField
      arr.sort((a, b) => {
        const va = a[key]
        const vb = b[key]
        if (va == null && vb == null) return 0
        if (va == null) return 1
        if (vb == null) return -1
        return va > vb ? dir : (va < vb ? -dir : 0)
      })
      return arr
    },
    canQuery () {
      if (!this.formKey || !this.year) return false
      if (this.reportType === 1 && !this.month) return false
      if (this.reportType === 2 && !this.quarter) return false
      return true
    }
  },
  async created () {
    await this.loadInitData()
  },
  methods: {
    async loadInitData () {
      try {
        const [waveRes, formRes] = await Promise.all([
          getWaveList(this.projectId),
          getProjectForms(this.projectId)
        ])
        const waveWrapper = (waveRes.data && waveRes.data.data) || waveRes.data || waveRes
        const allWaves = waveWrapper.rows || (Array.isArray(waveWrapper) ? waveWrapper : [])
        this.allFormList = (formRes.data && formRes.data.data) || formRes.data || []

        // 收集"第3阶段问卷"
        const formIdSet = new Set()
        allWaves.forEach(w => {
          ;(w.phases || []).forEach(p => {
            const phaseNo = parseInt(p.phaseNo || 0)
            if (phaseNo === 3) {
              const fid = String(p.formId || '')
              if (fid) formIdSet.add(fid)
            }
          })
        })
        this.formList = this.allFormList.filter(f => formIdSet.has(String(f.id || '')))

        // 默认选第一个问卷
        if (this.formList.length > 0) {
          // 尝试恢复上次选择
          let prefer = null
          try { prefer = localStorage.getItem(`store_report_formKey_${this.projectId}`) } catch {}
          const exists = prefer && this.formList.find(f => this.getFormKey(f) === prefer)
          this.formKey = exists ? prefer : this.getFormKey(this.formList[0])
        }

        // 加载战区/单元
        await this.loadDaquXiaoquList()

        // 加载所有快照信息，以便联动月份/季度
        await this.loadAvailableSources()

        // 自动选最近一期
        this.autoPickLatestPeriod()

        // 加载数据
        if (this.canQuery) await this.loadData()
      } catch (e) {
        console.error('[StoreReport] loadInitData', e)
      }
    },

    /**
     * v1.5 修复 P1-03：从 /Report/regions 拉真实战区/单元列表。
     * 后端按用户数据权限过滤。xiaoqu 上挂 daquId 用于联动。
     */
    async loadDaquXiaoquList () {
      try {
        const res = await axios.post('/Report/regions', {
          ProjectId: parseInt(this.projectId)
        })
        const wrapper = (res && res.data) || res || {}
        const data = wrapper.data || wrapper
        this.daquList = (data.daqus || []).map(d => ({ id: d.id, name: d.name }))
        this.xiaoquList = (data.xiaoqus || []).map(x => ({ id: x.id, name: x.name, daquId: x.daquId }))
      } catch (e) {
        console.error('[StoreReport] loadDaquXiaoquList', e)
        this.daquList = []
        this.xiaoquList = []
      }
    },

    async loadAvailableSources () {
      if (!this.formKey) { this.allSources = []; return }
      try {
        const res = await axios.post('/Report/availableSources', {
          ProjectId: this.projectId,
          FormKey: this.formKey
        })
        const wrapper = (res && res.data) || res
        this.allSources = (wrapper.items || (wrapper.data && wrapper.data.items) || []).map(it => ({
          snapshotId: it.snapshotId,
          reportType: it.reportType,
          year: it.year,
          month: it.month,
          quarter: it.quarter,
          periodKey: it.periodKey,
          periodLabel: it.periodLabel,
          storeCount: it.storeCount,
          isPublished: it.isPublished,
          generateTime: it.generateTime,
          scoringRuleCode: it.scoringRuleCode
        }))
      } catch (e) {
        console.error('[StoreReport] loadAvailableSources', e)
        this.allSources = []
      }
    },

    autoPickLatestPeriod () {
      // 切换 reportType 时，自动选该粒度下最新一期；若没有则保留默认（月度=本月，季度/年度=null）
      const matches = (this.allSources || []).filter(s =>
        s.reportType === this.reportType && s.year === this.year)
      if (this.reportType === 1) {
        if (matches.length) {
          this.month = Math.max.apply(null, matches.map(m => m.month).filter(Boolean))
        } else {
          // 月度允许走实时计算 → 默认本月
          this.month = new Date().getMonth() + 1
        }
        this.quarter = null
      } else if (this.reportType === 2) {
        this.quarter = matches.length
          ? Math.max.apply(null, matches.map(m => m.quarter).filter(Boolean))
          : null
        this.month = null
      } else {
        this.month = null
        this.quarter = null
      }
    },

    onReportTypeChange () {
      this.autoPickLatestPeriod()
    },

    onYearChange () {
      this.month = null
      this.quarter = null
      this.loadAvailableSources().then(() => this.autoPickLatestPeriod())
    },

    onFormChange () {
      try { localStorage.setItem(`store_report_formKey_${this.projectId}`, this.formKey) } catch {}
      this.allSources = []
      this.loadAvailableSources().then(() => {
        this.autoPickLatestPeriod()
        if (this.canQuery) this.loadData()
      })
    },

    onDaquChange () { this.xiaoquId = null },

    handleQuery () {
      if (!this.canQuery) {
        this.$message.warning('请完整选择查询条件')
        return
      }
      this.page = 1
      this.loadData()
    },

    handleReset () {
      const cur = new Date()
      this.reportType = 1
      this.year = cur.getFullYear()
      this.month = null
      this.quarter = null
      this.daquId = null
      this.xiaoquId = null
      this.storeKeyword = ''
      this.metricType = 1
      this.page = 1
      this.autoPickLatestPeriod()
    },

    onPageChange (page) {
      this.page = page
      this.loadData()
    },
    onSizeChange (size) {
      this.pageSize = size
      this.page = 1
      this.loadData()
    },
    /**
     * v1.5 修复 P2-03：接入排序回调。当前页内排序，
     * 字段为 prop（avgScore / daquRankInNational / xiaoquRankInDaqu）。
     */
    onSortChange ({ prop, order }) {
      this.sortField = prop || ''
      this.sortOrder = order || ''
    },

    async loadData () {
      if (!this.canQuery) return
      this.loading = true
      this.errorMsg = ''
      try {
        const params = {
          ProjectId: parseInt(this.projectId),
          FormKey: this.formKey,
          ReportType: this.reportType,
          Year: this.year,
          Month: this.reportType === 1 ? this.month : null,
          Quarter: this.reportType === 2 ? this.quarter : null,
          MetricType: this.metricType,
          DaquId: this.daquId || null,
          XiaoquId: this.xiaoquId || null,
          StoreKeyword: this.storeKeyword || '',
          Page: this.page,
          PageSize: this.pageSize
        }

        // Step 1: 优先快照（季度/年度必走，月度先尝试）
        const snapRes = await axios.post('/Report/snapshot', params)
        const snapWrap = (snapRes && snapRes.data) || snapRes
        const snapData = snapWrap.data || snapWrap
        if (snapData && snapData.hasSnapshot && snapData.data) {
          const data = snapData.data
          this.snapshotInfo = {
            generateTime: snapData.generateTime,
            storeCount: snapData.storeCount,
            isPublished: snapData.isPublished || false,
            publishTime: snapData.publishTime,
            snapshotId: snapData.snapshotId,
            periodLabel: data.periodLabel || buildPeriodLabel(this.reportType, this.year, this.month, this.quarter)
          }
          this.applyReportData(data, true)
          return
        }

        // Step 2: 季度/年度无快照 → 提示用户生成
        if (this.reportType > 1) {
          this.snapshotInfo = null
          this.applyReportData(null)
          this.errorMsg = '该期次报告尚未生成，请点击「生成/更新统计」按钮'
          return
        }

        // Step 3: 月度兜底 → 实时计算
        this.snapshotInfo = null
        const res = await axios.post('/Report/regional', params)
        const wrapper = (res && res.data) || res
        const data = wrapper.data || wrapper
        if (data && !data.errcode) {
          this.applyReportData(data, false)
        } else {
          this.applyReportData(null)
          this.errorMsg = (data && data.message) || '加载失败'
        }
      } catch (e) {
        console.error('[StoreReport] loadData', e)
        this.errorMsg = '加载数据失败'
      } finally {
        this.loading = false
      }
    },

    applyReportData (data, fromSnapshot) {
      if (!data) {
        this.rows = []
        this.segmentColumns = []
        this.itemColumns = []
        this.optionRates = {}
        this.total = 0
        return
      }
      const ctx = {
        businessType: this.businessType,
        businessTypeLabel: this.businessTypeLabel || this.currentFormName,
        year: this.year,
        month: this.month,
        quarter: this.quarter
      }
      this.rows = flattenStoreRows(data, ctx)
      this.segmentColumns = ensureLetterCodes(data.segmentColumns || [])
      this.itemColumns = data.itemColumns || []
      this.optionRates = data.itemOptionRates || {}
      this.scoringRuleCode = data.scoringRuleCode || 'simple'
      this.scoringRuleLabel = data.scoringRuleLabel || RULE_LABELS[this.scoringRuleCode] || this.scoringRuleCode
      this.total = data.total || (this.rows ? this.rows.length : 0)
    },

    async onGenerateSuccess (info) {
      // 生成弹窗回调：刷新可用快照 + 同步查询条件 + 重新加载
      if (info) {
        this.reportType = info.reportType || this.reportType
        this.year = info.year || this.year
        this.month = info.month || null
        this.quarter = info.quarter || null
      }
      await this.loadAvailableSources()
      await this.loadData()
      this.$message.success('报告已更新')
    },

    async publishReport () {
      if (!this.snapshotInfo) {
        this.$message.warning('请先生成快照')
        return
      }
      this.publishDialogVisible = true
    },

    async confirmPublish () {
      if (!this.snapshotInfo) return
      this.publishing = true
      try {
        const res = await axios.post('/Report/publish', {
          ProjectId: parseInt(this.projectId),
          FormKey: this.formKey,
          // 兼容旧接口：传一个虚拟 WaveId（=0），后端按 SnapshotId 处理
          WaveId: 0,
          SnapshotId: this.snapshotInfo.snapshotId,
          ReportType: this.reportType
        })
        const data = (res && res.data && res.data.data) || (res && res.data) || res
        if (data && !data.errcode) {
          this.$message.success('发布成功')
          this.snapshotInfo.isPublished = true
          this.publishDialogVisible = false
        } else {
          this.$message.error((data && data.message) || '发布失败')
        }
      } catch (e) {
        console.error(e)
        this.$message.error('发布失败')
      } finally {
        this.publishing = false
      }
    },

    async unpublishReport () {
      try {
        await this.$confirm('取消发布后客户门户将无法查看，确定吗？', '取消发布', {
          type: 'warning'
        })
      } catch { return }

      this.publishing = true
      try {
        const res = await axios.post('/Report/unpublish', {
          ProjectId: parseInt(this.projectId),
          FormKey: this.formKey,
          SnapshotId: this.snapshotInfo && this.snapshotInfo.snapshotId
        })
        const data = (res && res.data && res.data.data) || (res && res.data) || res
        if (data && !data.errcode) {
          this.$message.success('已取消发布')
          if (this.snapshotInfo) this.snapshotInfo.isPublished = false
        } else {
          this.$message.error((data && data.message) || '操作失败')
        }
      } catch (e) {
        console.error(e)
        this.$message.error('操作失败')
      } finally {
        this.publishing = false
      }
    },

    /**
     * v1.5 修复 P1-02：导出"查询结果全量"。
     * 不再依赖当前分页 rows，重新发起 PageSize=99999 的请求拿到全量数据。
     */
    async exportExcel () {
      if (!this.hasData) {
        this.$message.warning('暂无数据可导出')
        return
      }

      // 重新拉全量数据（保留当前所有筛选条件）
      let exportRows = this.rows || []
      let exportSegmentColumns = this.segmentColumns || []
      let exportItemColumns = this.itemColumns || []
      let exportOptionRates = this.optionRates || {}
      try {
        const params = {
          ProjectId: parseInt(this.projectId),
          FormKey: this.formKey,
          ReportType: this.reportType,
          Year: this.year,
          Month: this.reportType === 1 ? this.month : null,
          Quarter: this.reportType === 2 ? this.quarter : null,
          MetricType: this.metricType,
          DaquId: this.daquId || null,
          XiaoquId: this.xiaoquId || null,
          StoreKeyword: this.storeKeyword || '',
          Page: 1,
          PageSize: 99999
        }
        const exportLoading = this.$loading({ lock: true, text: '正在导出全量数据...', background: 'rgba(255,255,255,0.7)' })
        try {
          // 优先尝试快照
          const snapRes = await axios.post('/Report/snapshot', params)
          const snapWrap = (snapRes && snapRes.data) || snapRes
          const snapData = snapWrap.data || snapWrap
          let data = null
          if (snapData && snapData.hasSnapshot && snapData.data) {
            data = snapData.data
          } else {
            const res = await axios.post('/Report/regional', params)
            const wrapper = (res && res.data) || res
            data = wrapper.data || wrapper
          }
          if (data && !data.errcode) {
            const ctx = {
              businessType: this.businessType,
              businessTypeLabel: this.businessTypeLabel || this.currentFormName,
              year: this.year,
              month: this.month,
              quarter: this.quarter
            }
            exportRows = flattenStoreRows(data, ctx)
            exportSegmentColumns = ensureLetterCodes(data.segmentColumns || [])
            exportItemColumns = data.itemColumns || []
            exportOptionRates = data.itemOptionRates || {}
          }
        } finally {
          exportLoading.close()
        }
      } catch (e) {
        console.warn('[StoreReport] 全量导出请求失败，退化为当前页导出', e)
      }

      const fixedHeaders = ['序号', '业务类型', '年度', '期次', '战区', '单元', '门店编号', '门店简称',
        '总体得分', '全国排名', '战区排名']
      const fixedCount = fixedHeaders.length

      // 5 行表头
      const headerRows = [[], [], [], [], []]
      for (let i = 0; i < fixedCount; i++) {
        headerRows[0][i] = fixedHeaders[i]
        headerRows[1][i] = ''
        headerRows[2][i] = ''
        headerRows[3][i] = ''
        headerRows[4][i] = ''
      }

      let col = fixedCount
      const topSegStartCol = col

      // v1.5 修复 P2-02：一级指标列在占比 Tab 下加注释，避免与"落实率"混淆
      const segHeaderLabel = this.metricType === 3
        ? '一级指标 (占比口径不适用)'
        : '一级指标'

      // 一级指标（仅 Level=1）
      const exportTopSegments = (exportSegmentColumns || []).filter(s => {
        const lvl = s.level ?? s.Level
        return lvl == null || lvl === 1
      })
      exportTopSegments.forEach(seg => {
        headerRows[0][col] = segHeaderLabel
        headerRows[1][col] = seg.letterCode || ''
        headerRows[2][col] = seg.segmentName || ''
        headerRows[3][col] = ''
        headerRows[4][col] = ''
        col++
      })
      const topSegEndCol = col - 1

      // 二级指标 / 题项
      const itemStartCol = col
      ;(exportItemColumns || []).forEach(item => {
        headerRows[0][col] = '二级指标'
        headerRows[1][col] = item.topSegmentLetter || ''
        headerRows[2][col] = item.topSegmentName || ''
        headerRows[3][col] = (item.serialNo || '') + (item.isBonus ? ' +' : '')
        headerRows[4][col] = item.label || ''
        col++
      })
      const itemEndCol = col - 1

      const dataRows = (exportRows || []).map((row, idx) => {
        const fixed = [
          idx + 1,
          row.businessType || this.businessTypeLabel || '',
          this.year + '年',
          this.reportType === 1 ? (this.month + '月') : (this.reportType === 2 ? ('Q' + this.quarter) : '全年'),
          row.daquName || '',
          row.xiaoquName || '',
          row.storeCode || '',
          row.name || '',
          row.isVeto ? '0 (否决)' : (row.avgScore == null ? '—' : row.avgScore),
          row.daquRankInNational || '',
          row.xiaoquRankInDaqu || ''
        ]
        const segValues = exportTopSegments.map(seg => {
          if (this.metricType === 1) {
            const v = row.segmentScores && row.segmentScores[seg.segmentKey]
            return v == null ? '—' : v
          }
          if (this.metricType === 2) {
            const r = row.segmentScoreRates && row.segmentScoreRates[seg.segmentKey]
            return r == null ? '—' : (r + '%')
          }
          // v1.5 修复 P2-02：占比 Tab 下一级指标列没有"占比"语义，统一显示空，
          // 表头同步用"—（占比口径不适用）"提示，避免混用落实率被误读为占比。
          return '—'
        })
        const itemValues = (exportItemColumns || []).map(item => {
          const status = row.itemStatuses && row.itemStatuses[item.itemKey]
          if (status === 'NA') return '—'
          if (status === 'PENDING') return '·'
          if (row.isVeto) return '0'
          if (this.metricType === 1) {
            const v = row.itemScores && row.itemScores[item.itemKey]
            return v == null ? '—' : v
          }
          if (this.metricType === 2) {
            const r = row.itemScoreRates && row.itemScoreRates[item.itemKey]
            return r == null ? '—' : (r + '%')
          }
          // 占比
          const opts = exportOptionRates && exportOptionRates[item.itemKey]
          if (!opts || !opts.length) return '—'
          return opts.map(o => `${o.optionLabel || o.optionKey}:${(Number(o.rate) * 100).toFixed(1)}%`).join('; ')
        })
        return [...fixed, ...segValues, ...itemValues]
      })

      const aoa = [...headerRows, ...dataRows]
      const ws = XLSX.utils.aoa_to_sheet(aoa)

      // 固定列表头跨 5 行合并
      ws['!merges'] = ws['!merges'] || []
      for (let i = 0; i < fixedCount; i++) {
        ws['!merges'].push({ s: { r: 0, c: i }, e: { r: 4, c: i } })
      }

      // v1.5 修复 P2-02：动态表头分组合并
      // 行 0：整段"一级指标 / 二级指标"在水平方向合并
      if (topSegEndCol >= topSegStartCol) {
        ws['!merges'].push({ s: { r: 0, c: topSegStartCol }, e: { r: 0, c: topSegEndCol } })
      }
      if (itemEndCol >= itemStartCol) {
        ws['!merges'].push({ s: { r: 0, c: itemStartCol }, e: { r: 0, c: itemEndCol } })
      }

      // 二级指标区里把同一一级指标下连续题项的"父字母 / 父名"合并(行 1 / 行 2)
      let runStart = itemStartCol
      for (let i = 0; i < (exportItemColumns || []).length; i++) {
        const cur = exportItemColumns[i]
        const next = exportItemColumns[i + 1]
        const colIdx = itemStartCol + i
        const sameAsNext = next && (cur.topSegmentLetter || '') === (next.topSegmentLetter || '')
          && (cur.topSegmentName || '') === (next.topSegmentName || '')
        if (!sameAsNext) {
          if (colIdx > runStart) {
            ws['!merges'].push({ s: { r: 1, c: runStart }, e: { r: 1, c: colIdx } })
            ws['!merges'].push({ s: { r: 2, c: runStart }, e: { r: 2, c: colIdx } })
          }
          runStart = colIdx + 1
        }
      }

      const itemCount = (exportItemColumns || []).length
      const topSegCount = exportTopSegments.length
      ws['!cols'] = [
        { wch: 6 }, { wch: 10 }, { wch: 8 }, { wch: 8 }, { wch: 10 }, { wch: 12 },
        { wch: 14 }, { wch: 22 }, { wch: 12 }, { wch: 10 }, { wch: 10 },
        ...Array(topSegCount + itemCount).fill({ wch: 10 })
      ]

      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, '单店报告')

      const tabLabel = { 1: '得分', 2: '落实率', 3: '占比' }[this.metricType] || '报告'
      const dateStr = new Date().toISOString().slice(0, 10)
      const safeTitle = (this.reportTitle || '单店报告').replace(/[\\/:*?"<>|]/g, '_')
      XLSX.writeFile(wb, `${safeTitle}_${tabLabel}_${dateStr}.xlsx`)
      this.$message.success(`导出成功（${dataRows.length} 条门店）`)
    },

    getFormKey (form) {
      return form.formKey || form.key || form.sourceId || form.source_id || ''
    },
    stripHtml (html) {
      if (!html) return ''
      return String(html).replace(/<[^>]*>/g, '')
    }
  }
}
</script>

<style lang="scss" scoped>
.store-report {
  background: #f6f8fb;
  padding: 20px 24px;
  min-height: 100%;
}

.report-query-panel {
  background: #fff;
  border: 1px solid #e6ebf2;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 16px;

  .query-title {
    font-size: 13px;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 10px;
    padding-left: 6px;
    border-left: 3px solid #3f65d8;
    line-height: 16px;
  }
  .query-row {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;

    .qf {
      &.qf-narrow { width: 110px; }
      &.qf-form { min-width: 200px; flex: 1 1 240px; }
      &.qf-search { width: 200px; }
    }
  }
}

.report-title-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  margin-bottom: 8px;

  .report-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
  }
  .title-actions {
    display: flex;
    align-items: center;
    gap: 8px;

    .export-link {
      color: #3f65d8;
      padding: 0 6px;
      i { margin-right: 4px; }
    }
  }
}

.info-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 4px;
  margin-bottom: 8px;
  color: #64748b;
  font-size: 12px;

  .sep { color: #cbd5e1; }
}

.metric-tabs {
  margin-bottom: 12px;
  ::v-deep .el-radio-button__inner {
    border-radius: 0;
    background: #fff;
    border-color: #e6ebf2;
    color: #475569;
  }
  ::v-deep .el-radio-button:first-child .el-radio-button__inner {
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }
  ::v-deep .el-radio-button:last-child .el-radio-button__inner {
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }
  ::v-deep .el-radio-button__orig-radio:checked + .el-radio-button__inner {
    background: #3f65d8;
    border-color: #3f65d8;
    box-shadow: -1px 0 0 0 #3f65d8;
  }
}

.empty-tip {
  margin-top: 12px;
  padding: 32px 0;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
  background: #fff;
  border: 1px dashed #e2e8f0;
  border-radius: 8px;
  &.warn {
    color: #b45309;
    background: #fff7ed;
    border-color: #fed7aa;
  }
}
</style>
